import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { FileCheck, Lock, Save, Unlock } from 'lucide-react'
import BackLink from '../../components/BackLink'
import Layout from '../../components/Layout'
import RoundMatchupEditor from '../../components/admin/RoundMatchupEditor'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card'
import { Input } from '../../components/ui/input'
import { useNotification } from '../../contexts/NotificationContext'
import { buildEasternIsoString, utcToEastern } from '../../lib/dateUtils'
import {
  fetchBracketWithDetails,
  publishRoundMatchups,
  setBracketRound,
} from '../../services/bracketService'

const STATUS_META = {
  pending: { label: 'Not open', variant: 'orange', help: 'Nobody can pick. Use this until the matchups are announced.' },
  open: { label: 'Open for picks', variant: 'teal', help: 'Users can submit and edit picks for this round.' },
  locked: { label: 'Locked', variant: 'blue', help: 'Picks frozen. Enter the official results for this round.' },
}

function roundLabelFor(roundNum, matchCount) {
  if (roundNum === 0) return 'Pre-Qualifiers'
  if (matchCount === 1) return 'Championship'
  if (matchCount === 2) return 'Semifinals'
  if (matchCount === 4) return 'Quarterfinals'
  if (matchCount === 8) return 'Round of 16'
  return `Round ${roundNum}`
}

/**
 * Admin: per-round locking for a multi-week bracket.
 *
 * A tournament that runs over several weekends can't be published as one
 * finished bracket, because later matchups don't exist yet — in the MLP
 * playoffs the top-4 bye teams choose their quarterfinal opponent from the
 * first-round winners, and the quarterfinal winners choose again for the
 * semis. This page drives that cycle: open a round for picks with its own
 * deadline, let it lock, enter that round's official results, then publish the
 * next round's matchups and open it. Points accumulate across rounds on a
 * single entry, doubling each round, exactly as they already do.
 *
 * A bracket only opts into this once a round row exists; until then it behaves
 * as a normal single-deadline bracket.
 */
export default function BracketRounds() {
  const { bracketId } = useParams()
  const navigate = useNavigate()
  const notify = useNotification()

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [savingRound, setSavingRound] = useState(null)
  const [drafts, setDrafts] = useState({})

  const load = useCallback(async () => {
    try {
      setLoading(true)
      const result = await fetchBracketWithDetails(bracketId)
      setData(result)
      const next = {}
      ;(result?.rounds || []).forEach(r => {
        const { date, time } = utcToEastern(r.submission_deadline)
        next[r.round] = { date: date || '', time: time || '23:59' }
      })
      setDrafts(next)
    } catch (err) {
      console.error('Error loading bracket rounds:', err)
      notify.error('Failed to load bracket')
    } finally {
      setLoading(false)
    }
  }, [bracketId, notify])

  useEffect(() => { load() }, [load])

  // Rounds present in the bracket skeleton, whether or not they have a lock row
  // yet. The seed rows for rounds past the first are materialized server-side,
  // so a freshly built bracket may only show rounds 0-1 until then; derive the
  // full ladder from the base round's match count so every round is listed.
  const rounds = useMemo(() => {
    const seeds = data?.seeds || []
    if (!seeds.length) return []
    const byRound = {}
    seeds.forEach(s => {
      byRound[s.round] = (byRound[s.round] || 0) + 1
    })
    const roundNums = Object.keys(byRound).map(Number).sort((a, b) => a - b)
    const baseRound = roundNums.find(r => r > 0) ?? roundNums[0]
    const baseCount = byRound[baseRound] || 0
    const finalRound = baseCount > 1
      ? baseRound + Math.ceil(Math.log2(baseCount))
      : baseRound

    const lockRows = {}
    ;(data?.rounds || []).forEach(r => { lockRows[r.round] = r })

    const out = []
    if (byRound[0]) out.push({ round: 0, matchCount: byRound[0] })
    for (let r = baseRound; r <= finalRound; r++) {
      const derived = Math.max(1, Math.floor(baseCount / Math.pow(2, r - baseRound)))
      out.push({ round: r, matchCount: byRound[r] ?? derived })
    }
    return out.map(r => ({
      ...r,
      label: lockRows[r.round]?.label || roundLabelFor(r.round, r.matchCount),
      lock: lockRows[r.round] || null,
      status: lockRows[r.round]?.status || 'pending',
      matchupsPublished: !!lockRows[r.round]?.matchups_published,
    }))
  }, [data])

  const usesRoundLocks = (data?.rounds || []).length > 0

  const applyRound = async (roundNum, payload) => {
    try {
      setSavingRound(roundNum)
      await setBracketRound(bracketId, roundNum, payload)
      await load()
      notify.success('Round updated.')
    } catch (err) {
      console.error('Error updating round:', err)
      notify.error(err?.message || 'Failed to update round')
    } finally {
      setSavingRound(null)
    }
  }

  const handleSaveDeadline = (roundNum) => {
    const draft = drafts[roundNum] || {}
    if (!draft.date) {
      applyRound(roundNum, { clearDeadline: true })
      return
    }
    const iso = buildEasternIsoString(draft.date, draft.time || '23:59')
    if (!iso) {
      notify.error('Enter a valid date and time.')
      return
    }
    applyRound(roundNum, { deadline: iso })
  }

  const handlePublishMatchups = async (roundNum, matchups) => {
    try {
      setSavingRound(roundNum)
      await publishRoundMatchups(bracketId, roundNum, matchups)
      await load()
      notify.success('Matchups published. Open the round when you\'re ready for picks.')
    } catch (err) {
      console.error('Error publishing matchups:', err)
      notify.error(err?.message || 'Failed to publish matchups')
    } finally {
      setSavingRound(null)
    }
  }

  if (loading) {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-8 text-muted-foreground">Loading rounds…</div>
      </Layout>
    )
  }

  const tournamentId = data?.tournament?.id

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        {tournamentId && <BackLink label="Back to tournament" to={`/admin/tournaments/${tournamentId}/edit`} />}

        <Card>
          <CardHeader className="pb-3">
            <CardTitle>Round schedule</CardTitle>
            <CardDescription>
              {usesRoundLocks
                ? 'Each round opens, locks, and scores on its own schedule. Points carry across rounds on the same entry and double every round.'
                : 'This bracket uses a single deadline for the whole tournament. Give a round its own deadline below to switch it to round-by-round locking — useful when later matchups are not known up front.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {rounds.length === 0 && (
              <p className="text-sm text-muted-foreground">
                This bracket has no matches yet. Build it first, then come back to schedule the rounds.
              </p>
            )}

            {rounds.map(({ round, label, matchCount, status, matchupsPublished }) => {
              const meta = STATUS_META[status] || STATUS_META.pending
              const draft = drafts[round] || { date: '', time: '23:59' }
              const busy = savingRound === round
              // A round is ready to open once every slot has a team. Rounds
              // whose pairings the league assigns (quarterfinals onward) sit
              // with empty slots until they're published, so flag those rather
              // than letting an admin open a round nobody can pick.
              const seedsForRound = (data?.seeds || []).filter(s => s.round === round)
              const hasEmptySlot =
                seedsForRound.length < matchCount
                || seedsForRound.some(s => !s.team_one?.id || !s.team_two?.id)
              // The final's pairing is structural (the two semifinal winners),
              // so the server fills and publishes it from official results —
              // unlike the league-assigned rounds, there is nothing to author.
              const isFinal = matchCount === 1 && round === rounds[rounds.length - 1].round
              // The final never needs its matchup before opening: each entrant
              // picks their champion out of their own two semifinal picks, so
              // it should open alongside the semifinals (Perch b9cc1705).
              const needsMatchups = round >= 1 && !isFinal && !matchupsPublished && hasEmptySlot

              return (
                <div key={round} className="rounded-lg border border-border p-3 space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm">{label}</span>
                      <span className="text-xs text-muted-foreground">
                        {matchCount} {matchCount === 1 ? 'match' : 'matches'} · {round === 0 ? 1 : Math.pow(2, round - 1)} pt each
                      </span>
                    </div>
                    <Badge variant={meta.variant}>{meta.label}</Badge>
                  </div>

                  <p className="text-xs text-muted-foreground">{meta.help}</p>

                  <div className="flex flex-wrap items-end gap-2">
                    <div className="flex flex-col gap-1">
                      <label className="text-xs text-muted-foreground" htmlFor={`deadline-date-${round}`}>
                        Picks close (Eastern)
                      </label>
                      <div className="flex gap-2">
                        <Input
                          id={`deadline-date-${round}`}
                          type="date"
                          value={draft.date}
                          onChange={e => setDrafts(d => ({ ...d, [round]: { ...draft, date: e.target.value } }))}
                          className="w-[9.5rem]"
                        />
                        <Input
                          type="time"
                          value={draft.time}
                          onChange={e => setDrafts(d => ({ ...d, [round]: { ...draft, time: e.target.value } }))}
                          className="w-[7rem]"
                        />
                      </div>
                    </div>
                    <Button size="sm" variant="outline" disabled={busy} onClick={() => handleSaveDeadline(round)}>
                      <Save className="h-4 w-4 mr-1" /> Save deadline
                    </Button>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {status !== 'open' && (
                      <Button
                        size="sm"
                        disabled={busy || needsMatchups}
                        onClick={() => applyRound(round, { status: 'open', label })}
                        title={needsMatchups ? 'Publish this round\'s matchups first' : undefined}
                      >
                        <Unlock className="h-4 w-4 mr-1" /> Open for picks
                      </Button>
                    )}
                    {status !== 'locked' && (
                      <Button size="sm" variant="outline" disabled={busy} onClick={() => applyRound(round, { status: 'locked', label })}>
                        <Lock className="h-4 w-4 mr-1" /> Lock now
                      </Button>
                    )}
                    {status === 'locked' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/bracket/${bracketId}/picks?entryId=official&mode=editOfficial`)}
                      >
                        <FileCheck className="h-4 w-4 mr-1" /> Enter results
                      </Button>
                    )}
                  </div>

                  {needsMatchups && (
                    <p className="text-xs text-[var(--orange-11)]">
                      Matchups for this round are not set. Publish them below before opening it for picks.
                    </p>
                  )}

                  {isFinal && !matchupsPublished && (
                    <p className="text-xs text-muted-foreground">
                      Each player picks their champion out of their own two semifinal picks, so open
                      this round together with the semifinals. The real matchup fills in automatically
                      once the semifinal official results are entered.
                    </p>
                  )}

                  {round >= 1 && (
                    <RoundMatchupEditor
                      round={round}
                      matchCount={matchCount}
                      seeds={data?.seeds || []}
                      published={matchupsPublished}
                      disabled={busy}
                      onPublish={matchups => handlePublishMatchups(round, matchups)}
                    />
                  )}
                </div>
              )
            })}
          </CardContent>
        </Card>
      </div>
    </Layout>
  )
}
